import { bundlerNames } from '@ms-cloudpack/bundler';
import type { BundleMode } from '@ms-cloudpack/common-types';
import { InvalidArgumentError, Option } from 'commander';
import { parseMatch } from './parseMatch.js';
import { environmentInfo } from '@ms-cloudpack/environment';
import type { ReusedOptions } from '../types/ReusedOptions.js';
import { optionGroups } from './CloudpackHelp.js';

/**
 * These options are used by multiple commands, but should be manually added.
 */
export const reusedOptions = {
  /**
   * Specific app(s) to target. NOTE: This will only be respected if the `program.addSubCommand` call
   * also sets `discoverAppPaths`.
   */
  app: (options?: { multi?: boolean }) =>
    new Option(
      '--app <packageName>',
      `Run Cloudpack targeting ${options?.multi ? 'specific app(s)' : 'a specific app'} ` +
        '(prefers an exact match, or falls back to substrings).',
    ),
  bundler: () => new Option('--bundler <name>', 'Force using a specific bundler.').choices(bundlerNames),
  logBundleInfo: (options?: { requiresNoCache?: boolean }) =>
    new Option(
      '--log-bundle-info',
      "Write log files in each package's output folder with bundle input, output, and info." +
        (options?.requiresNoCache ? ' (Requires --no-cache if bundle output already exists.)' : ''),
    ).helpGroup(optionGroups.logging),
  logResolveMap: () =>
    new Option('--log-resolve-map', 'Log the resolve map to "resolve-map.json".').helpGroup(optionGroups.logging),
  /**
   * @param descriptionStart Start of description for this command, e.g. "Bundle specific package(s)"
   * (it will automatically include an optional version note and examples)
   */
  match: (descriptionStart: string) =>
    new Option(
      '--match <match...>',
      `${descriptionStart}, optionally with a version: e.g. "foo", "@foo/*", "foo@1.2.3"`,
    ).argParser(parseMatch),
  mode: (options: { action: 'bundle' | 'run' }) =>
    new Option(
      '--mode <mode>',
      `The mode to ${options.action === 'bundle' ? 'bundle the package' : 'run the app'} in. (library | production)`,
    )
      .argParser(parseBundleMode)
      .default('library'),
  /** `--no-cache` option */
  cache: () => new Option('--no-cache', 'Rebuild all packages, rather than using cached assets.'),
  login: (command: 'sync' | 'start') =>
    new Option(
      '--login <method>',
      `How to authenticate against the remote cache${command === 'start' ? ' (only if starting a remote app)' : ''}.`,
    )
      .choices(['interactive', 'azure-cli', 'device-code'])
      .default(getDefaultLogin(), '"azure-cli" in CI, "device-code" in Github Codespaces, "interactive" otherwise'),
  // Verify the type with `satisfies` to preserve individual function signatures.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<keyof ReusedOptions, (...args: any[]) => Option>;

/** Parse the session mode based on user input (e.g. --mode argument in start.) */
function parseBundleMode(mode: string): BundleMode {
  switch (mode.toLowerCase()) {
    case 'lib':
    case 'library':
      return 'library';
    case 'dev':
    case 'development':
      return 'development';
    case 'prod':
    case 'production':
      return 'production';
    default:
      throw new InvalidArgumentError(`Valid options are 'lib', 'dev', or 'prod'.`);
  }
}

export function getDefaultLogin(): string {
  return environmentInfo.isCI ? 'azure-cli' : environmentInfo.isCodespaces ? 'device-code' : 'interactive';
}
